import React, { useMemo, useState } from "react";
import { Button, Card, Input, Select, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SchoolTeachersProgress } from "../../../services/teacherProgress/teacherProgress.types";
import {
  ALL_GRADES,
  ALL_SUBJECTS,
  APP_LABELS,
  GradeFilter,
  SchoolProgressRow,
  SubjectFilter,
  buildGradeOptions,
  buildSubjectOptions,
  filterSchoolRows,
  flattenSchoolTeachers,
  formatLastViewedAt,
  formatTeacherName,
  formatWeekLabel,
  programsNeedingTitle,
  summarizeSchoolTeachers,
} from "../../../utils/teacherProgress/teacher-progress-display";
import styles from "./styles.module.scss";

interface Props {
  progress: SchoolTeachersProgress;
  onOpenTeacher: (teacherId: number) => void;
}

const NO_ACTIVITY = "Sin actividad";
const EMPTY_CELL = "—";

const SchoolProgressCard: React.FC<Props> = ({ progress, onOpenTeacher }) => {
  const [grade, setGrade] = useState<GradeFilter>(ALL_GRADES);
  const [subject, setSubject] = useState<SubjectFilter>(ALL_SUBJECTS);
  const [name, setName] = useState("");

  const allPrograms = useMemo(
    () => progress.teachers.flatMap(({ programs }) => programs),
    [progress],
  );
  const gradeOptions = useMemo(
    () => buildGradeOptions(allPrograms),
    [allPrograms],
  );
  const subjectOptions = useMemo(
    () => buildSubjectOptions(allPrograms),
    [allPrograms],
  );
  const titled = useMemo(
    () =>
      new Set(
        progress.teachers.flatMap(({ teacher, programs }) =>
          Array.from(programsNeedingTitle(programs)).map(
            (programId) => `${teacher.id}:${programId}`,
          ),
        ),
      ),
    [progress],
  );
  const allRows = useMemo(
    () => flattenSchoolTeachers(progress.teachers),
    [progress],
  );
  const rows = useMemo(
    () => filterSchoolRows(allRows, grade, subject, name),
    [allRows, grade, subject, name],
  );
  const summary = summarizeSchoolTeachers(progress.teachers);
  const showSchool =
    new Set(progress.teachers.map(({ teacher }) => teacher.school?.id)).size >
    1;

  const columns: ColumnsType<SchoolProgressRow> = [
    {
      title: "Profesor",
      key: "teacher",
      render: (_, { teacher }) => (
        <>
          <Button
            type="link"
            className={styles.teacherLink}
            onClick={() => onOpenTeacher(teacher.id)}
          >
            {formatTeacherName(teacher)}
          </Button>
          <small className={styles.programTitle}>
            {teacher.email ?? "Sin correo"}
          </small>
        </>
      ),
    },
    ...(showSchool
      ? [
          {
            title: "Plantel",
            key: "school",
            render: (_: unknown, { teacher }: SchoolProgressRow) =>
              teacher.school?.name ?? "Sin escuela",
          },
        ]
      : []),
    {
      title: "App",
      key: "app",
      render: (_, { program }) =>
        program ? APP_LABELS[program.app] : EMPTY_CELL,
    },
    {
      title: "Grado",
      key: "grade",
      render: (_, { program }) => program?.grade.name ?? EMPTY_CELL,
    },
    {
      title: "Asignatura",
      key: "subject",
      render: (_, { key, program }) =>
        program ? (
          <>
            {program.subject.name}
            {titled.has(key) && (
              <small className={styles.programTitle}>
                {program.programTitle}
              </small>
            )}
          </>
        ) : (
          EMPTY_CELL
        ),
    },
    {
      title: "Última semana vista",
      key: "week",
      render: (_, { program }) =>
        program ? (
          <Tag color="purple">{formatWeekLabel(program.week)}</Tag>
        ) : (
          <Typography.Text type="secondary">{NO_ACTIVITY}</Typography.Text>
        ),
    },
    {
      title: "Último acceso",
      key: "lastViewedAt",
      render: (_, { program }) =>
        program ? (
          formatLastViewedAt(program.lastViewedAt)
        ) : (
          <Typography.Text type="secondary">{NO_ACTIVITY}</Typography.Text>
        ),
    },
  ];

  return (
    <Card
      title={progress.school.name}
      extra={
        <div className={styles.filters}>
          <Input
            allowClear
            placeholder="Filtrar por profesor"
            style={{ width: 220 }}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Select
            aria-label="Grado"
            style={{ width: 200 }}
            value={grade}
            onChange={setGrade}
            options={gradeOptions}
          />
          <Select
            aria-label="Asignatura"
            style={{ width: 220 }}
            value={subject}
            onChange={setSubject}
            options={subjectOptions}
          />
        </div>
      }
    >
      <Typography.Text type="secondary" className={styles.summary}>
        {`${summary.teachers} ${
          summary.teachers === 1 ? "profesor" : "profesores"
        } · ${summary.inactive} sin actividad`}
      </Typography.Text>
      <Table
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: true }}
        locale={{
          emptyText:
            progress.teachers.length === 0
              ? "Esta escuela no tiene profesores."
              : "Ningún profesor coincide con los filtros.",
        }}
      />
      <small className={styles.note}>
        Los profesores sin actividad todavía no han abierto ningún programa.
      </small>
    </Card>
  );
};

export default SchoolProgressCard;
